import { useState } from "react";


type AbstractValueEditorProps = {
    initialValue: any;
    editing: boolean;
    textToValue: (text: string | null) => any;
    isTextValid: (text: string | null) => boolean;
    getText: () => string | null;
    setText: (text: string | null) => void;
    renderReadOnlyContent: (() => React.ReactNode) | null;
    renderEditingContent: (onBlur: () => void, onEnterKeyPress: (event: React.KeyboardEvent) => void, onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void, getText: () => string | null) => React.ReactNode;
    onValueChange: (value: any) => void;
    setEditing: (editing: boolean) => void;
}

export const VALIDATION_DELAY = 1000;

export function AbstractValueEditor({ initialValue, editing, textToValue, isTextValid, getText, setText, renderReadOnlyContent, renderEditingContent, onValueChange, setEditing }: AbstractValueEditorProps) {

    const [timer, setTimer] = useState<NodeJS.Timeout | undefined>(undefined);

    function validate(textValue: string | null | undefined) {
        let text = textValue;
        if ( text === undefined) {
            text = getText();
        }
        // if the text is not valid, do nothing
        if (!isTextValid(text)) {
            setEditing(false);
            return;
        }

        let value = textToValue(text);

        // if the value is the same as the item value, do nothing
        if (value === initialValue) {
            if (!textValue) {
                setEditing(false);
            }
            return;
        }

        onValueChange(value);
        if (textValue === undefined) {
            setEditing(false);
        }
    }

    function cancel() {
        setText(initialValue);
        setEditing(false);
    }

    const authorizedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "-", "Home", "End"];

    function onEnterKeyPressEditing(event: React.KeyboardEvent) {
        if (authorizedKeys.includes(event.key)) {
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            validate(undefined);
        } else if (event.key === "Tab") {
            event.preventDefault();
            event.stopPropagation();
            validate(undefined);
        } else if (event.key === "Escape") {
            event.preventDefault();
            cancel();
        } else {
            const newValue = getText() ? getText() + event.key : event.key;
            if (!isTextValid(newValue)) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    function onBlur() {
        validate(undefined);
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>) {
        let value = event.target.value;
        if (isTextValid(value)) {
            setText(value);
            clearTimeout(timer);

            const newTimer = setTimeout(() => {
                validate(value);
            }, VALIDATION_DELAY);

            setTimer(newTimer);
        } else {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    if (editing || !renderReadOnlyContent) {
        return renderEditingContent(onBlur, onEnterKeyPressEditing, onTextChange, getText);
    } else {
        return renderReadOnlyContent();
    }
}
